/**
 * Section-omission records on the persisted report (2026-08-11).
 *
 * "Unevidenced section HOLDS, never aborts" (863e50d05) gave the V2 document
 * model explicit omission records — but they are computed only at server
 * finalize (`buildReportDocumentV1`), so the section-depth auditors that read
 * the persisted `SiteRagReport` never see them. Live consequence (Redhead,
 * 2026-08-11): the public-sentiment report generated with 8 empty sections
 * because every attempted tier-1 sentiment source returned zero facts, and the
 * audit raised 16 BLOCK findings whose only offered fix — "Regenerate affected
 * section" — cannot ever succeed. Regeneration reproduces the same emptiness.
 *
 * This module stamps `qaFindings.sectionOmissions` at generation time so those
 * auditors can tell "the evidence does not exist" apart from "the generator
 * dropped a section". The probe is deliberately NARROW: an omission is
 * recorded only when the report's evidence domain demonstrably has nothing —
 * a generic "any empty section is omitted" rule would silently excuse real
 * generator regressions, which is exactly what the depth gate exists to catch.
 */
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import { SECTION_DEPTH_REQUIREMENTS } from '../../config/sectionDepthRequirements';
import { extractSectionsFromReport } from '../sectionDepthGate';
import { publicSentimentScreeningReadiness, STANCE_ONLY_SENTIMENT_SECTIONS, STANCE_LIMITED_CONTEXT_SECTIONS } from '../sentiment/publicSentimentScreening';
import { probePublicSentimentEvidence } from '../publicSentimentEvidenceProbe';
import type { ReportTypeId } from '../../config/reportTypes';

export interface SectionOmissionRecord {
  sectionId: string;
  reason: 'source-unavailable' | 'insufficient-evidence';
  detail: string;
}

/**
 * The evidence probe now lives in `../publicSentimentEvidenceProbe` so the
 * section-fallback composers can consult the SAME predicate. They run before
 * this stamp, and while they could not see it they filled every empty section
 * with a "pending re-run" placeholder — which made this module unreachable in
 * production, since a populated section is never omitted.
 *
 * An un-attempted scan is still NOT an omission case: the fix there is to run
 * the scan, and the coverage checks say so.
 */

/** Compute omission records for a report. Currently public-sentiment only. */
export function computeSectionOmissions(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
): SectionOmissionRecord[] {
  const reportType = (report.reportType ?? 'feasibility') as ReportTypeId;
  if (reportType !== 'public-sentiment') return [];

  const probe = probePublicSentimentEvidence(sed);
  if (!probe.empty) {
    const at = Date.parse(report.createdAt);
    const readiness = publicSentimentScreeningReadiness(sed, at);
    if (!Number.isFinite(at) || !readiness.ready || readiness.receipt.localStanceBasis !== 'no-dated-local-stance'
      || report.publicSentimentStatus !== 'unavailable' || report.sentimentIndex) return [];
    const sections = new Map(extractSectionsFromReport(report).map(row => [row.id, row.content.trim()]));
    return [...STANCE_ONLY_SENTIMENT_SECTIONS, ...STANCE_LIMITED_CONTEXT_SECTIONS]
      // Empty contact/outreach chapters remain a generation defect. Useful
      // roles and follow-up actions can be stated without assigning positions.
      .filter(sectionId => !STANCE_LIMITED_CONTEXT_SECTIONS.has(sectionId) || (sections.get(sectionId) ?? '').length > 0)
      .map(sectionId => ({ sectionId, reason: 'insufficient-evidence' as const,
        detail: `The completed ${readiness.receipt.fetchedAt} assessment established no dated local stance. Observed-position analysis is omitted, not scored as neutral. `
          + (STANCE_LIMITED_CONTEXT_SECTIONS.has(sectionId)
            ? 'The required contact or recommended-outreach content remains; it does not establish stakeholder positions.'
            : 'Available source context remains readable; additional attributable, dated local observations are needed for stance comparisons.') }));
  }

  const requirements = SECTION_DEPTH_REQUIREMENTS[reportType] ?? [];
  const sections = new Map(extractSectionsFromReport(report).map((s) => [s.id, s.content.trim()]));
  const detail =
    `The saved scan records ${probe.attempted} source attempts and contains no usable facts for this site ` +
    '(none, or classifier-baseline defaults only). Source failures and unread coverage remain unknown; ' +
    'the section requires additional verified evidence.';

  const out: SectionOmissionRecord[] = [];
  for (const req of requirements) {
    const content = sections.get(req.sectionId) ?? '';
    if (content.length > 0) continue; // a populated section is never omitted
    out.push({ sectionId: req.sectionId, reason: 'source-unavailable', detail });
  }
  return out;
}

/** Stamp omissions onto `qaFindings.sectionOmissions` (overwrites prior stamp). */
export function stampSectionOmissions(
  report: SiteRagReport,
  sed: SiteEvidenceDocument | null | undefined,
): SiteRagReport {
  const omissions = computeSectionOmissions(report, sed);
  if (omissions.length === 0) return report;
  return {
    ...report,
    qaFindings: {
      ...(report.qaFindings ?? {}),
      sectionOmissions: omissions,
    },
  };
}

/** Convenience: the omitted section-id set an auditor should demote against. */
export function omittedSectionIds(report: Pick<SiteRagReport, 'qaFindings'>): Set<string> {
  const rows = report.qaFindings?.sectionOmissions ?? [];
  return new Set(rows.map((r) => r.sectionId));
}

/** Rendering differs from stance-depth auditing: a qualified omission of
 * positions does not hide the saved contact roles and recommended outreach. */
export function hiddenReportSectionIds(report: SiteRagReport): Set<string> {
  const hidden = omittedSectionIds(report);
  const receipt = report.screeningBasis?.sentimentScan;
  const qualified = report.reportType === 'public-sentiment' && !report._metadataOnly
    && report.publicSentimentStatus === 'unavailable' && !report.sentimentIndex
    && receipt?.version === 'sentiment-screening-v1' && receipt.localStanceBasis === 'no-dated-local-stance'
    && report.screeningBasis?.sed.id === report.sedId && /^[a-f0-9]{64}$/.test(receipt.queryFingerprint)
    && Number.isFinite(Date.parse(receipt.fetchedAt)) && receipt.attemptedSources.length > 0;
  if (qualified) for (const id of STANCE_LIMITED_CONTEXT_SECTIONS) {
    const content = report[id as 'stakeholderMap' | 'mitigationPlaybook'];
    const omission = report.qaFindings?.sectionOmissions?.find(row => row.sectionId === id);
    if (typeof content === 'string' && content.trim() && omission?.reason === 'insufficient-evidence' && omission.detail.trim()) hidden.delete(id);
  }
  return hidden;
}
